import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Icon } from '@/components/icons/Icon'
import b from '@/components/ui/buttons.module.css'
import { Sheet } from '@/components/ui/Sheet'
import { showToast } from '@/components/ui/toastBus'
import { type LocationPermission, refreshLocation } from '@/features/location/refresh'
import { useLocationStore } from '@/features/location/store'
import { foundText, LOCATE_ERRORS, useLocateMe } from '@/features/location/useLocateMe'
import { useInstallMode, useInstallStore } from '@/features/pwa/install'

import { InstallSheet } from './InstallSheet'
import { markSeen, type PromptName, seenThisVisit } from './seen'
import s from './StartPrompts.module.css'

function LocationPrompt({ onClose }: { onClose: () => void }) {
  const { status, run } = useLocateMe()
  const error = status === 'idle' || status === 'locating' ? null : status
  const use = async () => {
    const found = await run()
    if (!found) return
    showToast(foundText(found))
    onClose()
  }
  return (
    <Sheet title="See the shops nearest to you" onClose={onClose}>
      <div className={s.body}>
        <span className={s.badge} aria-hidden="true">
          <Icon name="locate" size={26} />
        </span>
        <p className={s.text} role={error ? 'alert' : undefined}>
          {error
            ? LOCATE_ERRORS[error]
            : 'Allow your location once to see nearby shops first, how far each shop is, and start in your nearest town. We save it with your visits to improve townshop.in.'}
        </p>
        <button
          type="button"
          className={`${b.btn} ${b.primary} ${b.lg}`}
          disabled={status === 'locating'}
          onClick={() => void use()}
        >
          {status === 'locating' ? 'Finding you…' : 'Use my location'}
        </button>
        <button type="button" className={`${b.btn} ${b.ghost}`} onClick={onClose}>
          Not now
        </button>
      </div>
    </Sheet>
  )
}

/**
 * Start-up prompts (D-65, D-67). Location (BR-24): on each visit until done, at most once per
 * visit; it stops once the browser grants it (then it is read quietly), blocks it, or the shopper
 * chose "Stop using my location". The browser's own location dialog opens only from the tap
 * (D-05). The app (D-63): the sheet shows only ONCE per device, a few seconds after the page has
 * loaded; after that the menu's "Install app" offers it until installed.
 */
export function StartPrompts({
  delayMs = 1500,
  installDelayMs = 6000,
}: {
  delayMs?: number
  installDelayMs?: number
}) {
  const qc = useQueryClient()
  const coords = useLocationStore((st) => st.coords)
  const optedOut = useLocationStore((st) => st.optedOut)
  const installMode = useInstallMode()
  const installShown = useInstallStore((st) => st.promptShown)
  const markInstallShown = useInstallStore((st) => st.markPromptShown)
  const [permission, setPermission] = useState<LocationPermission | null>(null)
  const [seen, setSeen] = useState(seenThisVisit)
  const [open, setOpen] = useState<PromptName | null>(null)

  useEffect(() => {
    void refreshLocation(qc).then(setPermission)
  }, [qc])

  const wantLocation =
    permission !== null &&
    !coords &&
    !optedOut &&
    permission !== 'granted' &&
    permission !== 'denied' &&
    !seen.has('location')
  const wantInstall = installMode !== null && !installShown && !seen.has('install')
  // Location goes first; wait for the permission check before offering the app.
  const next: PromptName | null =
    permission === null ? null : wantLocation ? 'location' : wantInstall ? 'install' : null

  useEffect(() => {
    if (open || !next) return
    const timer = setTimeout(
      () => {
        if (next === 'install') markInstallShown()
        setOpen(next)
      },
      next === 'install' ? installDelayMs : delayMs,
    )
    return () => clearTimeout(timer)
  }, [open, next, delayMs, installDelayMs, markInstallShown])

  const close = () => {
    if (!open) return
    markSeen(open)
    setSeen(seenThisVisit())
    setOpen(null)
  }

  if (open === 'location') return <LocationPrompt onClose={close} />
  if (open === 'install' && installMode) return <InstallSheet mode={installMode} onClose={close} />
  return null
}
